import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ManageSponsors = () => {
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);

    const [activeSponsor, setActiveSponsor] = useState(null);
    const [fundAmount, setFundAmount] = useState('');
    const [fundLoading, setFundLoading] = useState(false);

    const [editingSponsor, setEditingSponsor] = useState(null);
    const [editSponsorForm, setEditSponsorForm] = useState({
        company_name: '', contact_name: '', email: '', phone: '', business_phone: '',
        gst_number: '', pan_number: '', company_address: '', business_type: '', kyc_status: ''
    });
    const [editLoading, setEditLoading] = useState(false);

    const fetchSponsors = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'fetch_sponsors');
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setSponsors(data.sponsors || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchSponsors();
        // eslint-disable-next-line
    }, []);

    const handleKycUpdate = async (sponsorId, status) => {
        if (!await customConfirm(`Mark this sponsor as ${status}?`, 'Confirm KYC Action', 'warning')) return;
        try {
            const fd = new FormData();
            fd.append('action', 'update_kyc_status');
            fd.append('sponsor_id', sponsorId);
            fd.append('kyc_status', status);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('KYC Status updated', 'Success', 'success');
                fetchSponsors();
            } else {
                await customAlert(data.message || 'Error updating KYC', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleAddFunds = async (e) => {
        e.preventDefault();
        if (!activeSponsor || !fundAmount || isNaN(fundAmount) || parseFloat(fundAmount) <= 0) {
            await customAlert('Please enter a valid amount', 'Invalid Input', 'warning');
            return;
        }
        setFundLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'add_wallet_funds');
            fd.append('sponsor_id', activeSponsor.id);
            fd.append('amount', fundAmount);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Funds added successfully', 'Success', 'success');
                setActiveSponsor(null);
                setFundAmount('');
                fetchSponsors();
            } else {
                await customAlert(data.message || 'Error adding funds', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        } finally {
            setFundLoading(false);
        }
    };

    const openEditSponsor = (sp) => {
        setEditingSponsor(sp);
        setEditSponsorForm({
            company_name: sp.company_name || '',
            contact_name: sp.contact_name || '',
            email: sp.email || '',
            phone: sp.phone || '',
            business_phone: sp.business_phone || '',
            gst_number: sp.gst_number || '',
            pan_number: sp.pan_number || '',
            company_address: sp.company_address || '',
            business_type: sp.business_type || '',
            kyc_status: sp.kyc_status || 'Pending'
        });
    };

    const handleUpdateSponsor = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'edit_sponsor_details');
            fd.append('sponsor_id', editingSponsor.id);
            Object.keys(editSponsorForm).forEach(k => fd.append(k, editSponsorForm[k]));

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_ads.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Sponsor details updated successfully!', 'Success', 'success');
                setEditingSponsor(null);
                fetchSponsors();
            } else {
                await customAlert(data.message || 'Error updating sponsor', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        } finally {
            setEditLoading(false);
        }
    };

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <h2 className="sa-page-title">Sponsor Management</h2>
                    <p className="sa-page-subtitle">Manage sponsor KYC verification and wallet billing manually.</p>
                </div>

                <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                        <table className="sa-table" style={{ margin: 0 }}>
                            <thead>
                                <tr>
                                    <th>Company & Contact</th>
                                    <th>KYC Details</th>
                                    <th>Wallet Balance</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '3rem' }}>Loading sponsors...</td>
                                    </tr>
                                ) : sponsors.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>No sponsors found.</td>
                                    </tr>
                                ) : (
                                    sponsors.map(sp => (
                                        <tr key={sp.id}>
                                            <td>
                                                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{sp.company_name}</div>
                                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '2px' }}>
                                                    {sp.email} <br/> {sp.phone || 'No Phone'}
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ 
                                                    display: 'inline-block', padding: '3px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '5px',
                                                    background: sp.kyc_status === 'Verified' ? 'rgba(34, 197, 94, 0.1)' : sp.kyc_status === 'Rejected' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                                                    color: sp.kyc_status === 'Verified' ? 'var(--success)' : sp.kyc_status === 'Rejected' ? 'var(--danger)' : 'var(--warning)'
                                                }}>
                                                    {sp.kyc_status || 'Pending'}
                                                </span>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                    <strong>GST:</strong> {sp.gst_number || 'N/A'}<br/>
                                                    <strong>PAN:</strong> {sp.pan_number || 'N/A'}<br/>
                                                    <strong>Type:</strong> {sp.business_type || 'N/A'}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'var(--success)' }}>
                                                    ₹{parseFloat(sp.wallet_balance || 0).toFixed(2)}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                    {sp.kyc_status === 'Pending' && (
                                                        <>
                                                            <button onClick={() => handleKycUpdate(sp.id, 'Verified')} className="sa-action-btn primary"><i className="fas fa-check"></i> Approve KYC</button>
                                                            <button onClick={() => handleKycUpdate(sp.id, 'Rejected')} className="sa-action-btn danger"><i className="fas fa-times"></i> Reject KYC</button>
                                                        </>
                                                    )}
                                                    <button onClick={() => openEditSponsor(sp)} className="sa-secondary-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>
                                                        <i className="fas fa-edit" style={{ marginRight: '5px' }}></i> Edit
                                                    </button>
                                                    <button onClick={() => setActiveSponsor(sp)} className="sa-secondary-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>
                                                        <i className="fas fa-wallet" style={{ marginRight: '5px' }}></i> Add Funds
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {activeSponsor && (
                    <div className="sa-modal-overlay">
                        <div className="sa-modal" style={{ maxWidth: '400px', width: '100%' }}>
                            <div className="sa-modal-header">
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}><i className="fas fa-money-bill-wave" style={{ color: 'var(--success)', marginRight: '10px' }}></i>Manual Wallet Funding</h3>
                                <button className="sa-modal-close" onClick={() => setActiveSponsor(null)}><i className="fas fa-times"></i></button>
                            </div>
                            <form onSubmit={handleAddFunds}>
                                <div style={{ padding: '15px 0' }}>
                                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '15px' }}>
                                        Add funds to <strong>{activeSponsor.company_name}</strong>. This will be recorded as a manual credit.
                                    </p>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Amount (₹) *</label>
                                        <input 
                                            type="number" 
                                            className="sa-input" 
                                            required 
                                            min="1"
                                            value={fundAmount} 
                                            onChange={e => setFundAmount(e.target.value)} 
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                    <button type="button" onClick={() => setActiveSponsor(null)} className="sa-secondary-btn">Cancel</button>
                                    <button type="submit" disabled={fundLoading} className="sa-primary-btn">{fundLoading ? 'Processing...' : 'Add Funds'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {editingSponsor && (
                    <div className="sa-modal-overlay">
                        <div className="sa-modal" style={{ maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div className="sa-modal-header">
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}><i className="fas fa-user-edit" style={{ color: 'var(--primary-color)', marginRight: '10px' }}></i>Edit Sponsor Details</h3>
                                <button className="sa-modal-close" onClick={() => setEditingSponsor(null)}><i className="fas fa-times"></i></button>
                            </div>
                            <form onSubmit={handleUpdateSponsor}>
                                <div style={{ padding: '15px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Company Name *</label>
                                        <input type="text" className="sa-input" required value={editSponsorForm.company_name} onChange={e => setEditSponsorForm({...editSponsorForm, company_name: e.target.value})} />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Contact Name</label>
                                        <input type="text" className="sa-input" value={editSponsorForm.contact_name} onChange={e => setEditSponsorForm({...editSponsorForm, contact_name: e.target.value})} />
                                    </div>
                                    
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Email Address *</label>
                                        <input type="email" className="sa-input" required value={editSponsorForm.email} onChange={e => setEditSponsorForm({...editSponsorForm, email: e.target.value})} />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">KYC Status</label>
                                        <select className="sa-input" value={editSponsorForm.kyc_status} onChange={e => setEditSponsorForm({...editSponsorForm, kyc_status: e.target.value})}>
                                            <option value="Pending">Pending</option>
                                            <option value="Verified">Verified</option>
                                            <option value="Rejected">Rejected</option>
                                        </select>
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Personal Phone</label>
                                        <input type="tel" className="sa-input" value={editSponsorForm.phone} onChange={e => setEditSponsorForm({...editSponsorForm, phone: e.target.value})} />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">Business Phone</label>
                                        <input type="tel" className="sa-input" value={editSponsorForm.business_phone} onChange={e => setEditSponsorForm({...editSponsorForm, business_phone: e.target.value})} />
                                    </div>

                                    <div className="sa-form-group">
                                        <label className="sa-form-label">GST Number</label>
                                        <input type="text" className="sa-input" value={editSponsorForm.gst_number} onChange={e => setEditSponsorForm({...editSponsorForm, gst_number: e.target.value.toUpperCase()})} />
                                    </div>
                                    <div className="sa-form-group">
                                        <label className="sa-form-label">PAN Number</label>
                                        <input type="text" className="sa-input" value={editSponsorForm.pan_number} onChange={e => setEditSponsorForm({...editSponsorForm, pan_number: e.target.value.toUpperCase()})} />
                                    </div>

                                    <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="sa-form-label">Business Type</label>
                                        <select className="sa-input" value={editSponsorForm.business_type} onChange={e => setEditSponsorForm({...editSponsorForm, business_type: e.target.value})}>
                                            <option value="">Select Type</option>
                                            <option value="Private Limited">Private Limited</option>
                                            <option value="Proprietorship">Proprietorship</option>
                                            <option value="Partnership">Partnership</option>
                                            <option value="LLP">LLP</option>
                                        </select>
                                    </div>

                                    <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="sa-form-label">Company Address</label>
                                        <textarea className="sa-input" style={{ resize: 'vertical', minHeight: '60px' }} value={editSponsorForm.company_address} onChange={e => setEditSponsorForm({...editSponsorForm, company_address: e.target.value})} />
                                    </div>

                                </div>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                    <button type="button" onClick={() => setEditingSponsor(null)} className="sa-secondary-btn">Cancel</button>
                                    <button type="submit" disabled={editLoading} className="sa-primary-btn">{editLoading ? 'Saving...' : 'Save Changes'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default ManageSponsors;
